import PermissionButton from 'components/buttons/PermissionButton';
import Loader from 'components/Loader';
import SearchBar from 'components/tables/list-table/filter-pieces/SearchBar';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAddCoiCheckMutation } from 'store/api/coi-check';
import { CoiCheckSummary } from 'types/coi-check';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import CoiCheckDetails from '../CoiCheckDetails';

const CHECK_PERMISSIONS = ['CREATE.COI_CHECKS'];

const CoiCheckView: React.FC = () => {
    const [searchText, setSearchText] = useState('');
    const [coiCheckSummary, setCoiCheckSummary] = useState<CoiCheckSummary | undefined>(undefined);
    const [addCoiCheck, { isLoading: isLoadingAddCoiCheck }] = useAddCoiCheckMutation();
    const [isSearchStarted, setIsSearchStarted] = useState(false);
    const canCheck = useHasPermissions(CHECK_PERMISSIONS);

    const handleSearchBarChange = useCallback((value = '') => {
        setSearchText(value);
        setIsSearchStarted(false);
    }, []);

    const runCheck = useCallback(
        (searchTerm: string): void => {
            if (searchTerm.length > 1) {
                setIsSearchStarted(true);
                addCoiCheck({ searchTerm })
                    .unwrap()
                    .then((res: CoiCheckSummary) => {
                        return setCoiCheckSummary(res);
                    })
                    // A failure is already toasted by the API layer.
                    .catch(() => undefined);
            }
        },
        [addCoiCheck]
    );

    const handleSearch = useCallback((): void => runCheck(searchText), [runCheck, searchText]);

    const handleDidYouMean = useCallback(
        (term: string): void => {
            setSearchText(term);
            runCheck(term);
        },
        [runCheck]
    );

    const renderCoiCheckDetails = (): JSX.Element => {
        if (isLoadingAddCoiCheck) {
            return <Loader />;
        }

        return coiCheckSummary ? <CoiCheckDetails summary={coiCheckSummary} onDidYouMean={handleDidYouMean} /> : <></>;
    };

    return (
        <>
            <div className="mb-2">{t('coi.instructions')}</div>
            {/* Enter in the field submits; the button is hidden without the permission, and so Enter starts no check. */}
            <form
                className="flex gap-4"
                onSubmit={(event) => {
                    event.preventDefault();
                    if (canCheck && !isSearchStarted) handleSearch();
                }}
            >
                <SearchBar
                    value={searchText}
                    onChange={(event) => handleSearchBarChange(event.target.value)}
                    pluralTextId="coi.records"
                    singularTextId="coi.record"
                    placeHolder={t('coi.search-term-placeholder')}
                    className="mb-2"
                    showSearchTips
                />
                <PermissionButton
                    type="submit"
                    permissions={CHECK_PERMISSIONS}
                    disabled={isSearchStarted}
                    variant="contained"
                    color="secondary"
                    size="small"
                >
                    <FormattedMessage id="coi.search" />
                </PermissionButton>
            </form>
            {isSearchStarted ? renderCoiCheckDetails() : <></>}
        </>
    );
};

export default CoiCheckView;
